import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["form", "weightMin", "weightMax", "priceMin", "priceMax", "weightSlider", "priceSlider", "sortColumn", "sortDirection"]

  connect() {
    // Bind the handler once and store it for proper cleanup
    this.boundHandleSortClick = this.handleSortClick.bind(this)
    this.boundShowLoading = this.showLoading.bind(this)
    this.boundHideLoading = this.hideLoading.bind(this)
    
    // Listen for clicks on sort links to capture sort parameters
    document.addEventListener('click', this.boundHandleSortClick, true)
    
    // Listen for turbo frame loading events on the specific frame
    const frame = document.getElementById('diamonds_list')
    if (frame) {
      frame.addEventListener('turbo:before-fetch-request', this.boundShowLoading)
      frame.addEventListener('turbo:frame-load', this.boundHideLoading)
      frame.addEventListener('turbo:frame-render', this.boundHideLoading)
      frame.addEventListener('turbo:fetch-request-error', this.boundHideLoading)
    }
    
    // Auto-submit on select changes
    this.element.querySelectorAll('select').forEach(select => {
      select.addEventListener('change', () => {
        this.updateRanges()
      })
    })

    // Listen to text field changes (stock number search)
    this.element.querySelectorAll('input[type="text"]').forEach(input => {
      input.addEventListener('input', () => {
        this.submitForm()
      })
    })

    // Listen to manual range input changes
    if (this.hasWeightMinTarget) {
      this.weightMinTarget.addEventListener('change', () => this.onWeightInputChange())
    }
    if (this.hasWeightMaxTarget) {
      this.weightMaxTarget.addEventListener('change', () => this.onWeightInputChange())
    }
    if (this.hasPriceMinTarget) {
      this.priceMinTarget.addEventListener('change', () => this.onPriceInputChange())
    }
    if (this.hasPriceMaxTarget) {
      this.priceMaxTarget.addEventListener('change', () => this.onPriceInputChange())
    }

    // Initialize range sliders after a short delay to ensure noUiSlider is loaded
    setTimeout(() => {
      if (typeof noUiSlider !== 'undefined') {
        this.initWeightSlider()
        this.initPriceSlider()
      }
    }, 100)
  }

  onWeightInputChange() {
    if (this.hasWeightSliderTarget && this.weightSliderTarget.noUiSlider) {
      const min = parseFloat(this.weightMinTarget.value) || this.weightSliderTarget.noUiSlider.options.range.min
      const max = parseFloat(this.weightMaxTarget.value) || this.weightSliderTarget.noUiSlider.options.range.max
      
      // Update slider position (won't trigger 'change' event, only 'update')
      this.weightSliderTarget.noUiSlider.set([min, max])
    }
    this.submitForm()
  }

  onPriceInputChange() {
    if (this.hasPriceSliderTarget && this.priceSliderTarget.noUiSlider) {
      const min = parseFloat(this.priceMinTarget.value) || this.priceSliderTarget.noUiSlider.options.range.min
      const max = parseFloat(this.priceMaxTarget.value) || this.priceSliderTarget.noUiSlider.options.range.max
      
      // Update slider position (won't trigger 'change' event, only 'update')
      this.priceSliderTarget.noUiSlider.set([min, max])
    }
    this.submitForm()
  }

  disconnect() {
    // Clean up event listeners
    if (this.boundHandleSortClick) {
      document.removeEventListener('click', this.boundHandleSortClick, true)
    }
    
    const frame = document.getElementById('diamonds_list')
    if (frame) {
      frame.removeEventListener('turbo:before-fetch-request', this.boundShowLoading)
      frame.removeEventListener('turbo:frame-load', this.boundHideLoading)
      frame.removeEventListener('turbo:frame-render', this.boundHideLoading)
      frame.removeEventListener('turbo:fetch-request-error', this.boundHideLoading)
    }
    
    // Clean up sliders when controller is removed
    if (this.hasWeightSliderTarget && this.weightSliderTarget.noUiSlider) {
      this.weightSliderTarget.noUiSlider.destroy()
    }
    if (this.hasPriceSliderTarget && this.priceSliderTarget.noUiSlider) {
      this.priceSliderTarget.noUiSlider.destroy()
    }
  }

  showLoading(event) {
    // Don't show loading for links that navigate away from the frame (turbo_frame: '_top')
    if (event?.target?.dataset?.turboFrame === '_top') {
      return
    }
    
    const frame = document.getElementById('diamonds_list')
    if (frame) {
      // Add semi-transparent overlay
      frame.style.position = 'relative'
      frame.style.opacity = '0.6'
      frame.style.pointerEvents = 'none'
      
      // Show spinner
      const spinner = frame.querySelector('[data-turbo-loading]')
      if (spinner) {
        spinner.classList.remove('d-none')
      }
      
      // Safety timeout: force hide after 10 seconds if events don't fire
      if (this.loadingTimeout) {
        clearTimeout(this.loadingTimeout)
      }
      this.loadingTimeout = setTimeout(() => {
        this.hideLoading()
      }, 10000)
    }
  }

  hideLoading(event) {
    // Clear safety timeout
    if (this.loadingTimeout) {
      clearTimeout(this.loadingTimeout)
      this.loadingTimeout = null
    }
    
    const frame = document.getElementById('diamonds_list')
    if (frame) {
      // Remove overlay
      frame.style.opacity = '1'
      frame.style.pointerEvents = 'auto'
      
      // Hide spinner
      const spinner = frame.querySelector('[data-turbo-loading]')
      if (spinner) {
        spinner.classList.add('d-none')
      }
    }
  }

  handleSortClick(event) {
    // Check if the clicked element is a sort link (has data-turbo-frame="diamonds_list")
    const link = event.target.closest('a[data-turbo-frame="diamonds_list"]')
    if (link) {
      // Extract sort parameters from the link's href
      const url = new URL(link.href)
      const sortColumn = url.searchParams.get('sort_column')
      const sortDirection = url.searchParams.get('sort_direction')
      
      // Only process if this is actually a sort link (has sort params)
      if (sortColumn && sortDirection) {
        // Update hidden fields immediately
        if (this.hasSortColumnTarget) {
          this.sortColumnTarget.value = sortColumn
        }
        if (this.hasSortDirectionTarget) {
          this.sortDirectionTarget.value = sortDirection
        }
      }
    }
  }

  async updateRanges() {
    // Get current filter values
    const formData = new FormData(this.formTarget)
    const params = new URLSearchParams(formData)
    
    // Fetch new ranges based on current filters
    try {
      const response = await fetch(`/lab-grown-diamonds/filter_ranges?${params}`)
      const data = await response.json()
      
      // Update and redraw sliders with new ranges
      this.updateWeightSlider(data.weight)
      this.updatePriceSlider(data.price)
      
      // Submit form to update the table
      this.submitForm()
    } catch (error) {
      console.error('Error fetching filter ranges:', error)
      this.submitForm()
    }
  }

  initWeightSlider() {
    if (!this.hasWeightSliderTarget) return
    if (typeof noUiSlider === 'undefined') return
    
    // Don't recreate if slider already exists
    if (this.weightSliderTarget.noUiSlider) return

    const rangeMin = parseFloat(this.weightSliderTarget.dataset.min) || 0.5
    const rangeMax = parseFloat(this.weightSliderTarget.dataset.max) || 10
    const currentMin = parseFloat(this.weightMinTarget.value) || rangeMin
    const currentMax = parseFloat(this.weightMaxTarget.value) || rangeMax

    // Non-linear range: give more weight to smaller carats (0.3-2.5 gets 75% of slider)
    // This provides better granularity where most diamonds are concentrated
    noUiSlider.create(this.weightSliderTarget, {
      start: [currentMin, currentMax],
      connect: true,
      range: {
        'min': rangeMin,
        '75%': 2.5,  // 75% of slider covers min to 2.5ct (where most diamonds are)
        'max': rangeMax
      },
      step: 0.01,
      // Format values to 2 decimal places
      format: {
        to: function(value) {
          return parseFloat(value).toFixed(2);
        },
        from: function(value) {
          return parseFloat(value);
        }
      }
    })

    // Update input fields while sliding (live update)
    this.weightSliderTarget.noUiSlider.on('update', (values) => {
      this.weightMinTarget.value = parseFloat(values[0]).toFixed(2)
      this.weightMaxTarget.value = parseFloat(values[1]).toFixed(2)
    })

    // Submit form only when user releases the slider
    this.weightSliderTarget.noUiSlider.on('change', () => {
      this.submitForm()
    })
  }

  initPriceSlider() {
    if (!this.hasPriceSliderTarget) return
    if (typeof noUiSlider === 'undefined') return
    
    // Don't recreate if slider already exists
    if (this.priceSliderTarget.noUiSlider) return

    const rangeMin = parseFloat(this.priceSliderTarget.dataset.min) || 100
    const rangeMax = parseFloat(this.priceSliderTarget.dataset.max) || 50000
    const currentMin = parseFloat(this.priceMinTarget.value) || rangeMin
    const currentMax = parseFloat(this.priceMaxTarget.value) || rangeMax

    // Non-linear range: give more weight to lower prices (min-$5000 gets 75% of slider)
    // This provides better granularity where most diamonds are priced
    noUiSlider.create(this.priceSliderTarget, {
      start: [currentMin, currentMax],
      connect: true,
      range: {
        'min': rangeMin,
        '75%': 5000,  // 75% of slider covers min to $5000 (where most diamonds are)
        'max': rangeMax
      },
      step: 10,
      // Format values as integers
      format: {
        to: function(value) {
          return Math.round(value);
        },
        from: function(value) {
          return parseFloat(value);
        }
      }
    })

    // Update input fields while sliding (live update)
    this.priceSliderTarget.noUiSlider.on('update', (values) => {
      this.priceMinTarget.value = Math.round(values[0])
      this.priceMaxTarget.value = Math.round(values[1])
    })

    // Submit form only when user releases the slider
    this.priceSliderTarget.noUiSlider.on('change', () => {
      this.submitForm()
    })
  }

  updateWeightSlider(newRange) {
    if (!this.hasWeightSliderTarget) return
    if (typeof noUiSlider === 'undefined') return

    // Round to nearest 0.01 for better precision
    const roundedMin = Math.floor(newRange.min * 100) / 100
    const roundedMax = Math.ceil(newRange.max * 100) / 100

    // Update data attributes
    this.weightSliderTarget.dataset.min = roundedMin
    this.weightSliderTarget.dataset.max = roundedMax

    // Update input values to new range (rounded)
    this.weightMinTarget.value = roundedMin.toFixed(2)
    this.weightMaxTarget.value = roundedMax.toFixed(2)

    // Destroy existing slider if it exists
    if (this.weightSliderTarget.noUiSlider) {
      this.weightSliderTarget.noUiSlider.destroy()
    }

    // Recreate slider with new range (will use non-linear configuration)
    this.initWeightSlider()
  }

  updatePriceSlider(newRange) {
    if (!this.hasPriceSliderTarget) return
    if (typeof noUiSlider === 'undefined') return

    // Round to nearest 10 for better precision
    const roundedMin = Math.floor(newRange.min / 10) * 10
    const roundedMax = Math.ceil(newRange.max / 10) * 10

    // Update data attributes
    this.priceSliderTarget.dataset.min = roundedMin
    this.priceSliderTarget.dataset.max = roundedMax

    // Update input values to new range (rounded)
    this.priceMinTarget.value = roundedMin
    this.priceMaxTarget.value = roundedMax

    // Destroy existing slider if it exists
    if (this.priceSliderTarget.noUiSlider) {
      this.priceSliderTarget.noUiSlider.destroy()
    }

    // Recreate slider with new range (will use non-linear configuration)
    this.initPriceSlider()
  }

  submitForm() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      // Show loading before form submits
      this.showLoading()
      this.formTarget.requestSubmit()
    }, 300)
  }

  clearFilters(event) {
    event.preventDefault()
    
    // Reset all text inputs
    this.element.querySelectorAll('input[type="text"]').forEach(input => {
      input.value = ''
    })
    
    // Reset all checkboxes (shape, color, clarity filters)
    this.element.querySelectorAll('input[type="checkbox"]').forEach(checkbox => {
      checkbox.checked = false
      const label = checkbox.closest('label')
      if (label) {
        label.classList.remove('btn-primary')
        label.classList.add('btn-outline-primary')
      }
    })
    
    // Reset all selects to first option
    this.element.querySelectorAll('select').forEach(select => {
      select.selectedIndex = 0
    })
    
    // Reset sort fields
    if (this.hasSortColumnTarget) {
      this.sortColumnTarget.value = ''
    }
    if (this.hasSortDirectionTarget) {
      this.sortDirectionTarget.value = ''
    }
    
    // Reset sliders to full range
    if (this.hasWeightSliderTarget && this.weightSliderTarget.noUiSlider) {
      const weightMin = parseFloat(this.weightSliderTarget.dataset.min)
      const weightMax = parseFloat(this.weightSliderTarget.dataset.max)
      this.weightSliderTarget.noUiSlider.set([weightMin, weightMax])
      this.weightMinTarget.value = weightMin.toFixed(2)
      this.weightMaxTarget.value = weightMax.toFixed(2)
    }
    
    if (this.hasPriceSliderTarget && this.priceSliderTarget.noUiSlider) {
      const priceMin = parseFloat(this.priceSliderTarget.dataset.min)
      const priceMax = parseFloat(this.priceSliderTarget.dataset.max)
      this.priceSliderTarget.noUiSlider.set([priceMin, priceMax])
      this.priceMinTarget.value = Math.round(priceMin)
      this.priceMaxTarget.value = Math.round(priceMax)
    }
    
    // Submit the cleared form
    this.formTarget.requestSubmit()
  }
}
